import React from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import api from 'api';
import { IReport, IReportPayload, IReportsPath } from 'api/reports/types';
import { TRANSACTIONS_KEYS } from 'api/transactions/constants';
import { STATUSES } from 'constants/common';
import { useAppDispatch } from 'hooks/storeHooks';
import { setReportDownloadFlag } from 'store/userConfiguration';
import { clampFutureEndDates } from 'utils/filters';
import { notificationService } from 'utils/notificationService';
import { QueryError } from 'utils/types';

import { normalizeQueryKey } from '../utils';

export const useCreateReport = (path?: string) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const prefix = 'common.notifications.report.create';

  const mutationFn = ({
    columns,
    needFilters,
    otherParams,
    pageFilters,
  }: IReportPayload) =>
    api.reports.createReport({
      columns,
      needFilters,
      otherParams,
      pageFilters: clampFutureEndDates(pageFilters),
      path,
    });

  return useMutation({
    mutationFn,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;

      notificationService.show({
        message:
          error?.response?.data?.message ||
          (isSavingError ? t('notifications.invalidData') : t(`${prefix}.error`)),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: context?.ntfID,
      });
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t(`${prefix}.loading`),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_payload, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.REPORTS]),
      });
      if (path) {
        dispatch(setReportDownloadFlag({ [path]: true }));
      }

      notificationService.show({
        message: t(`${prefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
    throwOnError: false,
  });
};
export const useDeleteReport = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const prefix = 'common.notifications.report.delete';

  return useMutation({
    mutationFn: api.reports.deleteReport,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;

      notificationService.show({
        message:
          error?.response?.data?.message ||
          (isSavingError ? t('notifications.invalidData') : t(`${prefix}.error`)),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: context?.ntfID,
      });
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t(`${prefix}.loading`),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_payload, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.REPORTS]),
      });
      notificationService.show({
        message: t(`${prefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
    throwOnError: false,
  });
};

export const useGetReports = ({ path }: IReportsPath): UseQueryResult<IReport[]> => {
  const queryFn = React.useCallback(() => api.reports.getReports({ path }), [path]);

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.REPORTS, path]),
  });
};
